import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Typography from '@/design-system/atoms/Typography';
import { trackInteraction } from '@/utils/analytics';
import { horizontalTabbedListKeyboardNavigation } from '@/utils/horizontalListKeyboardNavigation';

import {
    useProviderSearchContext,
    useProviderSearchZipCityContext,
} from '../../contexts/ProviderSearchContext';
import styles from './ProviderScheduling.module.scss';

const ITEMS = [
    { id: 'new', text: 'New Patient' },
    { id: 'existing', text: 'Existing Patient' },
];

/**
 * The Provider Scheduling component presents a selector that lets the user specify
 * if they're a new or existing patient
 */
const ProviderScheduling = ({ id, schedulingOverrideLabels, analytics, searchValue }) => {
    const tabsRef = React.useRef([]);
    const { handleScheduling, patientType } = useProviderSearchContext();
    const { zipCode } = useProviderSearchZipCityContext();

    const { newPatientLabel, existingPatientLabel } = schedulingOverrideLabels;

    const analyticsObj = React.useMemo(() => {
        return {
            ...analytics,
            componentName: 'fad_results_scheduling_module_controls',
            zipCode: zipCode || '',
            searchTerm: searchValue || '',
            interactionType: 'fad_result_filter',
            eventName: 'search',
        };
    }, [analytics, searchValue, zipCode]);

    /**
     * Handle selecting a patient type
     * @param {string} type The selected patient type
     * @returns {void}
     */
    const handlePatientTypeSelection = React.useCallback(
        (type) => {
            if (handleScheduling) handleScheduling(type);

            //analytics
            const optionLabel = ITEMS?.find((item) => item.id === type)?.text;
            trackInteraction({ ...analyticsObj, actionLabel: optionLabel });
        },
        [handleScheduling, analyticsObj],
    );

    /**
     * Handle keyboard navigation for tabs
     * @param {KeyboardEvent} e The keyboard event
     * @param {number} currentIndex The current tab index
     * @returns {void}
     */
    const handleKeyDown = React.useCallback(
        (e, currentIndex) => {
            const navigationHandler = horizontalTabbedListKeyboardNavigation({
                currentIndex,
                totalItems: ITEMS.length,
                wrap: true,
                onNavigate: (newIndex) => {
                    const selectedType = ITEMS[newIndex]?.id;
                    if (selectedType) {
                        handlePatientTypeSelection(selectedType);
                        tabsRef.current[newIndex]?.focus();
                    }
                },
            });

            navigationHandler(e);
        },
        [handlePatientTypeSelection],
    );

    return (
        <section className={styles[`provider-scheduling`]} id={id}>
            <div
                className={styles[`provider-scheduling__controls`]}
                role="tablist"
                aria-label="Patient type selection"
            >
                {ITEMS.map((item, index) => {
                    const isSelected = patientType === item.id;
                    const label = item.id === 'new' ? newPatientLabel : existingPatientLabel;

                    return (
                        <button
                            key={item.id}
                            ref={(node) => {
                                tabsRef.current[index] = node;
                            }}
                            type="button"
                            role="tab"
                            aria-selected={isSelected ? 'true' : 'false'}
                            aria-label={`Select ${label}`}
                            tabIndex={isSelected ? 0 : -1}
                            className={classnames(
                                styles[`provider-scheduling__tab`],
                                isSelected && styles[`provider-scheduling__tab--selected`],
                            )}
                            onClick={() => handlePatientTypeSelection(item.id)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                        >
                            <div className={styles[`provider-scheduling__tab-typography-wrapper`]}>
                                <Typography
                                    variant="t1"
                                    content={label}
                                    className={styles[`provider-scheduling__tab-typography`]}
                                />
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

ProviderScheduling.defaultProps = {
    id: 'patient-type',
    schedulingOverrideLabels: {
        eyebrow: 'Schedule Online',
        description: 'Are you a new or existing patient?',
        newPatientLabel: 'New Patient',
        existingPatientLabel: 'Returning Patient',
        dropdownLabel: 'Appointment Type',
    },
};

ProviderScheduling.propTypes = {
    id: PropTypes.string,
    schedulingOverrideLabels: PropTypes.shape({
        eyebrow: PropTypes.string,
        description: PropTypes.string,
        newPatientLabel: PropTypes.string,
        existingPatientLabel: PropTypes.string,
        dropdownLabel: PropTypes.string,
    }),
    searchValue: PropTypes.string,
    analytics: PropTypes.object,
};

export default ProviderScheduling;
